import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { Card, Button, Input } from '@shared/components/ui';
import { Building2, Plus, Globe, Mail, Hash, ChevronRight, X } from 'lucide-react';
import { getRoleTone } from '@shared/config/theme';
import { StatusChip } from '@shared/components/StatusChip';

interface Organization {
  id: string;
  name: string;
  slug: string;
  billing_email: string;
  data_region: string;
  created_at: string;
  updated_at: string;
  role: string;
  joined_at: string;
}

interface OrganizationsResponse {
  organizations: Organization[];
}

export default function Organizations() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [dataRegion, setDataRegion] = useState('GLOBAL');
  const [creating, setCreating] = useState(false);

  const formatRole = (role: string) => {
    const roleKeys: Record<string, string> = {
      OWNER: 'owner',
      ADMIN: 'admin',
      MEMBER: 'member',
      VIEWER: 'viewer',
    };
    const key = roleKeys[role.toUpperCase()];
    return key ? t(`organizations.roles.${key}`) : role;
  };

  const formatRegion = (region: string) => {
    const regionKeys: Record<string, string> = {
      GLOBAL: 'global',
      US: 'us',
      EU: 'eu',
    };
    const key = regionKeys[region.toUpperCase()];
    return key ? t(`organizations.new.regions.${key}`) : region;
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError('');
      setLoadError(false);
      const response = await api.get<OrganizationsResponse>('/organizations');
      setOrganizations(response.data.organizations || []);
    } catch (err: any) {
      setLoadError(true);
      if (err.response?.status !== 401) {
        setError(t('organizations.errors.loadListFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const createOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgName.trim() || !billingEmail.trim()) return;

    try {
      setCreating(true);
      await api.post('/organizations', {
        name: orgName,
        billing_email: billingEmail,
        data_region: dataRegion,
      });
      setOrgName('');
      setBillingEmail('');
      setDataRegion('GLOBAL');
      setShowCreateForm(false);
      fetchOrganizations();
    } catch (err: any) {
      setError(err.response?.data?.error || t('organizations.errors.createFailed'));
      setLoadError(false);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-300">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
              {t('organizations.title')}
            </h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('organizations.subtitle')}
            </p>
          </div>
          {!showCreateForm && (
            <Button onClick={() => setShowCreateForm(true)} size="lg" className="shadow-brand-200">
              <Plus className="w-5 h-5 mr-2" strokeWidth={3} /> {t('organizations.createButton')}
            </Button>
          )}
        </div>

        {/* Create Organization Form */}
        {showCreateForm && (
          <Card className="max-w-xl mx-auto shadow-elevated">
            <div className="p-5 sm:p-8 space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                  {t('organizations.new.title')}
                </h2>
                <button
                  type="button"
                  aria-label={t('common.close')}
                  onClick={() => setShowCreateForm(false)}
                  className="min-h-11 min-w-11 inline-flex items-center justify-center text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-300"
                >
                  <span className="sr-only">{t('common.close')}</span>
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={createOrganization} className="space-y-6">
                <Input
                  label={t('organizations.new.nameLabel')}
                  id="orgName"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder={t('organizations.new.namePlaceholder')}
                  required
                />

                <Input
                  label={t('organizations.new.billingEmailLabel')}
                  type="email"
                  id="billingEmail"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  placeholder={t('organizations.new.billingEmailPlaceholder')}
                  required
                />

                <div>
                  <label
                    htmlFor="dataRegion"
                    className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                  >
                    {t('organizations.new.dataRegionLabel')}
                  </label>
                  <select
                    id="dataRegion"
                    value={dataRegion}
                    onChange={(e) => setDataRegion(e.target.value)}
                    className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                  >
                    <option value="GLOBAL">{t('organizations.new.regions.global')}</option>
                    <option value="US">{t('organizations.new.regions.us')}</option>
                    <option value="EU">{t('organizations.new.regions.eu')}</option>
                  </select>
                  <p className="mt-2 text-xs font-medium text-brand-600 dark:text-brand-400 bg-accent-soft-light dark:bg-accent-soft-dark p-2 rounded-lg inline-block">
                    {t('organizations.new.dataRegionNote', {
                      region:
                        dataRegion === 'GLOBAL'
                          ? t('organizations.new.regions.global')
                          : dataRegion,
                    })}
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="secondary"
                    className="flex-1"
                    onClick={() => setShowCreateForm(false)}
                    type="button"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button className="flex-1" type="submit" isLoading={creating}>
                    {t('organizations.new.createButton')}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        )}

        {/* Error message */}
        {error && (
          <div
            role="alert"
            className="flex flex-col gap-3 rounded-xl border border-state-danger-light/20 bg-state-danger-bg-light p-4 text-sm font-medium text-state-danger-light dark:border-state-danger-dark/20 dark:bg-state-danger-bg-dark dark:text-state-danger-dark sm:flex-row sm:items-center sm:justify-between"
          >
            {error}
            {loadError && (
              <Button
                type="button"
                variant="outline"
                className="min-h-11 shrink-0"
                onClick={() => void fetchOrganizations()}
              >
                {t('organizations.errors.retry')}
              </Button>
            )}
          </div>
        )}

        {/* Organizations list */}
        {loading ? (
          <div className="py-20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
            <p className="text-text-subtle-light dark:text-text-subtle-dark font-bold uppercase tracking-widest text-xs">
              {t('organizations.loading')}
            </p>
          </div>
        ) : loadError ? null : organizations.length === 0 ? (
          <Card className="py-24 text-center border-2 border-dashed">
            <div className="w-20 h-20 bg-surface-2-light dark:bg-surface-2-dark rounded-3xl flex items-center justify-center mx-auto mb-6 text-text-subtle-light dark:text-text-subtle-dark">
              <Building2 size={40} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {t('organizations.empty.title')}
            </h3>
            <p className="text-text-muted-light dark:text-text-muted-dark mt-2 max-w-sm mx-auto font-medium text-sm">
              {t('organizations.empty.description')}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {organizations.map((org) => (
              <Card
                key={org.id}
                className="group p-0 transition-all hover:border-brand-300 dark:hover:border-brand-700"
              >
                <button
                  type="button"
                  className="w-full rounded-xl p-6 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:focus-visible:ring-brand-400"
                  onClick={() => navigate(`/organizations/${org.id}`)}
                >
                  <div className="flex justify-between items-start mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-accent-soft-light dark:bg-accent-soft-dark flex items-center justify-center text-brand-600 dark:text-brand-400">
                      <Building2 size={24} />
                    </div>
                    <StatusChip
                      tone={getRoleTone(org.role)}
                      label={formatRole(org.role)}
                      className="uppercase tracking-wider"
                    />
                  </div>

                  <div className="mb-6">
                    <h3 className="text-lg font-black text-slate-900 dark:text-white truncate">
                      {org.name}
                    </h3>
                    <div className="flex items-center text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark mt-1">
                      <Hash size={12} className="mr-1" />
                      <span className="font-mono">{org.slug}</span>
                    </div>
                  </div>

                  <div className="space-y-3 pt-6 border-t border-surface-border-light dark:border-surface-border-dark">
                    <div className="flex flex-col items-start gap-1 text-sm sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-text-muted-light dark:text-text-muted-dark font-medium flex items-center">
                        <Mail size={14} className="mr-2" /> {t('organizations.card.billing')}
                      </span>
                      <span
                        className="w-full break-all text-left font-bold text-slate-900 dark:text-white sm:max-w-[200px] sm:text-right"
                        title={org.billing_email}
                      >
                        {org.billing_email}
                      </span>
                    </div>
                    <div className="flex flex-col items-start gap-1 text-sm sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-text-muted-light dark:text-text-muted-dark font-medium flex items-center">
                        <Globe size={14} className="mr-2" /> {t('organizations.card.region')}
                      </span>
                      <span className="font-bold text-slate-900 dark:text-white">
                        {formatRegion(org.data_region)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center text-brand-600 dark:text-brand-400 text-xs font-black uppercase tracking-widest group-hover:translate-x-1 transition-transform">
                    {t('organizations.card.manage')} <ChevronRight size={14} className="ml-1" />
                  </div>
                </button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
